import { z } from 'zod';
import { isoDateSchema, listQuerySchema, paginationQuerySchema } from './common.js';
import { profileSlugSchema, profileSummarySchema } from './game-profile.js';
import { daemonSystemSchema } from './health.js';

// ---------- locations ----------

export const locationSchema = z.object({
  id: z.number().int(),
  short: z.string(),
  long: z.string().nullable(),
  nodeCount: z.number().int(),
  createdAt: isoDateSchema,
});
export type Location = z.infer<typeof locationSchema>;

export const locationInputSchema = z.object({
  short: z
    .string()
    .trim()
    .min(1)
    .max(60)
    .regex(/^[\w.-]+$/, 'Use letters, numbers, dots, dashes and underscores.'),
  long: z.string().trim().max(191).nullish(),
});

// ---------- nodes ----------

const hostnameRe =
  /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/i;
const ipv4Re = /^(25[0-5]|2[0-4]\d|1?\d?\d)(\.(25[0-5]|2[0-4]\d|1?\d?\d)){3}$/;

export const nodeInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).nullish(),
  locationId: z.number().int().positive(),
  fqdn: z
    .string()
    .trim()
    .refine((v) => hostnameRe.test(v) || ipv4Re.test(v), 'Enter a hostname or IPv4 address.'),
  scheme: z.enum(['http', 'https']),
  behindProxy: z.boolean().default(false),
  daemonPort: z.number().int().min(1).max(65535).default(4200),
  sftpPort: z.number().int().min(1).max(65535).default(2222),
  memoryMb: z.number().int().min(128),
  memoryOverallocate: z.number().int().min(-1).max(1000).default(0),
  diskMb: z.number().int().min(1024),
  diskOverallocate: z.number().int().min(-1).max(1000).default(0),
  uploadSizeMb: z.number().int().min(1).max(10_240).default(100),
  maintenanceMode: z.boolean().default(false),
});
export type NodeInput = z.input<typeof nodeInputSchema>;

export const nodeHealthSchema = z.object({
  online: z.boolean(),
  lastSeenAt: isoDateSchema.nullable(),
  error: z.string().nullable(),
  system: daemonSystemSchema.nullable(),
});

export const nodeSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  location: z.object({ id: z.number().int(), short: z.string() }),
  fqdn: z.string(),
  scheme: z.enum(['http', 'https']),
  behindProxy: z.boolean(),
  daemonPort: z.number().int(),
  sftpPort: z.number().int(),
  memoryMb: z.number().int(),
  memoryOverallocate: z.number().int(),
  diskMb: z.number().int(),
  diskOverallocate: z.number().int(),
  uploadSizeMb: z.number().int(),
  maintenanceMode: z.boolean(),
  tokenId: z.string(),
  allocated: z.object({ memoryMb: z.number().int(), diskMb: z.number().int() }),
  serverCount: z.number().int(),
  allocationCount: z.number().int(),
  health: nodeHealthSchema,
  createdAt: isoDateSchema,
});
export type NodeSummary = z.infer<typeof nodeSchema>;

export const nodeConfigurationSchema = z.object({
  yaml: z.string(),
});

export const deployTokenSchema = z.object({
  token: z.string(),
  expiresAt: isoDateSchema,
  command: z.string(),
});

// ---------- allocations ----------

export const allocationSchema = z.object({
  id: z.number().int(),
  ip: z.string(),
  ipAlias: z.string().nullable(),
  port: z.number().int(),
  notes: z.string().nullable(),
  server: z.object({ id: z.number().int(), name: z.string(), identifier: z.string() }).nullable(),
});
export type Allocation = z.infer<typeof allocationSchema>;

export const MAX_PORTS_PER_REQUEST = 1000;

/** "25565, 25570-25575" → [25565, 25570, ..., 25575]. Throws with a readable message. */
export function parsePortList(input: string): number[] {
  const ports = new Set<number>();
  for (const part of input.split(/[\s,]+/).filter(Boolean)) {
    const m = part.match(/^(\d{1,5})(?:-(\d{1,5}))?$/);
    if (!m) throw new Error(`"${part}" is not a port or range.`);
    const start = Number(m[1]);
    const end = m[2] ? Number(m[2]) : start;
    if (start < 1024 || end > 65535 || end < start) {
      throw new Error(`"${part}" must be within 1024-65535, low to high.`);
    }
    for (let p = start; p <= end; p++) {
      ports.add(p);
      if (ports.size > MAX_PORTS_PER_REQUEST)
        throw new Error(`At most ${MAX_PORTS_PER_REQUEST} ports at a time.`);
    }
  }
  if (ports.size === 0) throw new Error('Enter at least one port.');
  return [...ports].sort((a, b) => a - b);
}

export const createAllocationsSchema = z.object({
  ip: z
    .string()
    .trim()
    .refine((v) => ipv4Re.test(v), 'Enter an IPv4 address (0.0.0.0 binds every interface).'),
  ipAlias: z.string().trim().max(191).nullish(),
  ports: z.string().superRefine((value, ctx) => {
    try {
      parsePortList(value);
    } catch (err) {
      ctx.addIssue({ code: 'custom', message: (err as Error).message });
    }
  }),
});

export const updateAllocationSchema = z.object({
  ipAlias: z.string().trim().max(191).nullish(),
  notes: z.string().trim().max(256).nullish(),
});

export const allocationListQuerySchema = paginationQuerySchema.extend({
  filter: z.enum(['all', 'assigned', 'free']).default('all'),
});

export const bulkDeleteSchema = z.object({ ids: z.array(z.number().int()).min(1).max(1000) });

// ---------- nests & eggs ----------

export const nestInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  description: z.string().trim().max(500).nullish(),
});

export const nestSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  author: z.string(),
  eggCount: z.number().int(),
});
export type Nest = z.infer<typeof nestSchema>;

export const nestListQuerySchema = listQuerySchema.extend({
  /** `eggs` embeds each nest's egg summaries. */
  include: z.enum(['eggs']).optional(),
});
export const nodeListQuerySchema = listQuerySchema.extend({
  locationId: z.coerce.number().int().positive().optional(),
});
export const eggListQuerySchema = listQuerySchema.extend({
  nestId: z.coerce.number().int().positive().optional(),
});

export const eggVariableSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  description: z.string(),
  envVariable: z.string(),
  defaultValue: z.string(),
  userViewable: z.boolean(),
  userEditable: z.boolean(),
  rules: z.string(),
  sort: z.number().int(),
  /** Rules Hearth can't check (shown as a warning). */
  unknownRules: z.array(z.string()),
});
export type EggVariable = z.infer<typeof eggVariableSchema>;

export const eggVariableInputSchema = z.object({
  name: z.string().trim().min(1).max(191),
  description: z.string().trim().max(2000).default(''),
  envVariable: z
    .string()
    .trim()
    .regex(
      /^[A-Za-z_][A-Za-z0-9_]*$/,
      'Use letters, numbers and underscores, starting with a letter.',
    )
    .refine((v) => !RESERVED_ENV.has(v.toUpperCase()), 'This name is reserved by Hearth.'),
  defaultValue: z.string().max(10_000).default(''),
  userViewable: z.boolean().default(true),
  userEditable: z.boolean().default(true),
  rules: z.string().trim().min(1).max(500).default('nullable|string'),
  sort: z.number().int().default(0),
});

/** Variables the daemon sets itself. */
export const RESERVED_ENV = new Set([
  'STARTUP',
  'SERVER_MEMORY',
  'SERVER_IP',
  'SERVER_PORT',
  'P_SERVER_UUID',
  'TZ',
  'HOME',
  'USER',
  'PATH',
]);

export const imageArchSchema = z.record(
  z.string(),
  z.object({
    platforms: z.array(z.string()),
    checkedAt: isoDateSchema,
    error: z.string().optional(),
  }),
);

export const eggSummarySchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  nestId: z.number().int(),
  name: z.string(),
  description: z.string().nullable(),
  author: z.string(),
  dockerImages: z.record(z.string(), z.string()),
  serverCount: z.number().int(),
});
export type EggSummary = z.infer<typeof eggSummarySchema>;

export const nestWithEggsSchema = nestSchema.extend({ eggs: z.array(eggSummarySchema).optional() });
export type NestWithEggs = z.infer<typeof nestWithEggsSchema>;

export const eggDetailSchema = eggSummarySchema.extend({
  features: z.array(z.string()),
  fileDenylist: z.array(z.string()),
  updateUrl: z.string().nullable(),
  startup: z.string(),
  configFiles: z.string(),
  configStartup: z.string(),
  configLogs: z.string(),
  configStop: z.string(),
  configFromId: z.number().int().nullable(),
  scriptInstall: z.string(),
  scriptContainer: z.string(),
  scriptEntry: z.string(),
  copyScriptFromId: z.number().int().nullable(),
  /** The game profile pinned to this egg; null lets matching decide. */
  profileSlug: z.string().nullable(),
  /** The profile servers of this egg get (the pin when it is enabled, else matching). */
  profile: profileSummarySchema,
  imageArch: imageArchSchema,
  variables: z.array(eggVariableSchema),
  updatedAt: isoDateSchema,
});
export type EggDetail = z.infer<typeof eggDetailSchema>;

const jsonText = (label: string) =>
  z
    .string()
    .max(100_000)
    .refine((v) => {
      if (!v.trim()) return true;
      try {
        JSON.parse(v);
        return true;
      } catch {
        return false;
      }
    }, `${label} must be valid JSON.`);

export const eggInputSchema = z.object({
  nestId: z.number().int().positive(),
  name: z.string().trim().min(1).max(191),
  description: z.string().trim().max(5000).nullish(),
  author: z.string().trim().min(1).max(191),
  features: z.array(z.string().trim().min(1).max(64)).max(50).default([]),
  dockerImages: z
    .record(z.string().trim().min(1).max(100), z.string().trim().min(1).max(255))
    .refine((v) => Object.keys(v).length > 0, 'Add at least one docker image.'),
  fileDenylist: z.array(z.string().trim().min(1).max(255)).max(200).default([]),
  updateUrl: z.url().nullish(),
  startup: z.string().trim().min(1).max(10_000),
  configFiles: jsonText('Config files').default(''),
  configStartup: jsonText('Startup configuration').default(''),
  configLogs: jsonText('Log configuration').default(''),
  configStop: z.string().max(255).default(''),
  configFromId: z.number().int().positive().nullish(),
  scriptInstall: z.string().max(100_000).default(''),
  scriptContainer: z.string().trim().min(1).max(255),
  scriptEntry: z.string().trim().min(1).max(64),
  copyScriptFromId: z.number().int().positive().nullish(),
  /** Pin a game profile; null clears the pin, leaving it out keeps the current one. */
  profileSlug: profileSlugSchema.nullish(),
});
export type EggInput = z.input<typeof eggInputSchema>;

export const eggImportSchema = z.object({
  nestId: z.number().int().positive(),
  /** Parsed JSON of a Pterodactyl egg file. */
  egg: z.unknown(),
  /** Replace this egg instead of creating a new one. */
  replaceEggId: z.number().int().positive().optional(),
});

export const imageInspectSchema = z.object({ nodeId: z.number().int().positive() });

// ---------- servers ----------

export const serverStatusSchema = z
  .enum([
    'installing',
    'install_failed',
    'reinstall_failed',
    'suspended',
    'restoring_backup',
    'transferring',
  ])
  .nullable();

export const serverLimitsSchema = z.object({
  memoryMb: z.number().int().min(0),
  swapMb: z.number().int().min(-1),
  diskMb: z.number().int().min(0),
  ioWeight: z.number().int().min(10).max(1000).default(500),
  cpuPercent: z.number().int().min(0).max(100_000),
  threads: z
    .string()
    .trim()
    .regex(/^\d+(-\d+)?(,\d+(-\d+)?)*$/, 'Use a CPU list like 0-1 or 0,2.')
    .nullish()
    .or(z.literal('')),
  oomDisabled: z.boolean().default(false),
});

export const featureLimitsSchema = z.object({
  databases: z.number().int().min(0).max(1000).default(0),
  allocations: z.number().int().min(0).max(1000).default(0),
  backups: z.number().int().min(0).max(1000).default(0),
});

export const daemonServerStateSchema = z.object({
  state: z.enum(['offline', 'starting', 'running', 'stopping']),
  installing: z.boolean(),
  installed: z.boolean(),
  platform: z.string().optional(),
  emulated: z.boolean(),
});

const allocationRef = z.object({
  id: z.number().int(),
  ip: z.string(),
  ipAlias: z.string().nullable(),
  port: z.number().int(),
});

export const adminServerSchema = z.object({
  id: z.number().int(),
  uuid: z.string(),
  identifier: z.string(),
  name: z.string(),
  description: z.string(),
  status: serverStatusSchema,
  owner: z.object({ id: z.number().int(), username: z.string(), email: z.string() }),
  node: z.object({ id: z.number().int(), name: z.string() }),
  nest: z.object({ id: z.number().int(), name: z.string() }),
  egg: z.object({ id: z.number().int(), name: z.string() }),
  profile: profileSummarySchema,
  allocation: allocationRef,
  additionalAllocations: z.array(allocationRef),
  limits: z.object({
    memoryMb: z.number().int(),
    swapMb: z.number().int(),
    diskMb: z.number().int(),
    ioWeight: z.number().int(),
    cpuPercent: z.number().int(),
    threads: z.string().nullable(),
    oomDisabled: z.boolean(),
  }),
  featureLimits: z.object({
    databases: z.number().int(),
    allocations: z.number().int(),
    backups: z.number().int(),
  }),
  startup: z.string(),
  image: z.string(),
  skipScripts: z.boolean(),
  autoRestart: z.boolean(),
  installedAt: isoDateSchema.nullable(),
  createdAt: isoDateSchema,
});
export type AdminServer = z.infer<typeof adminServerSchema>;

export const serverVariableSchema = z.object({
  envVariable: z.string(),
  name: z.string(),
  description: z.string(),
  value: z.string(),
  defaultValue: z.string(),
  rules: z.string(),
  userViewable: z.boolean(),
  userEditable: z.boolean(),
});

export const adminServerDetailSchema = adminServerSchema.extend({
  variables: z.array(serverVariableSchema),
  eggImages: z.record(z.string(), z.string()),
  /** Live state from the daemon, or null with daemonError if it couldn't be reached. */
  daemon: daemonServerStateSchema.nullable(),
  daemonError: z.string().nullable(),
});
export type AdminServerDetail = z.infer<typeof adminServerDetailSchema>;

export const adminServerListQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(191).optional(),
  nodeId: z.coerce.number().int().optional(),
  ownerId: z.coerce.number().int().optional(),
});

export const createServerSchema = z.object({
  name: z.string().trim().min(1).max(191),
  description: z.string().trim().max(2000).default(''),
  ownerId: z.number().int().positive(),
  nodeId: z.number().int().positive(),
  eggId: z.number().int().positive(),
  allocationId: z.number().int().positive(),
  additionalAllocationIds: z.array(z.number().int().positive()).max(100).default([]),
  limits: serverLimitsSchema,
  featureLimits: featureLimitsSchema.default({ databases: 0, allocations: 0, backups: 0 }),
  image: z.string().trim().min(1).max(255),
  /** Defaults to the egg's startup command. */
  startup: z.string().trim().max(10_000).optional(),
  variables: z.record(z.string(), z.string()).default({}),
  skipScripts: z.boolean().default(false),
  startOnCompletion: z.boolean().default(false),
  autoRestart: z.boolean().default(true),
});
export type CreateServerInput = z.input<typeof createServerSchema>;

export const updateServerDetailsSchema = z.object({
  name: z.string().trim().min(1).max(191),
  description: z.string().trim().max(2000).default(''),
  ownerId: z.number().int().positive(),
});

export const updateServerBuildSchema = z.object({
  limits: serverLimitsSchema,
  featureLimits: featureLimitsSchema,
  allocationId: z.number().int().positive(),
  addAllocationIds: z.array(z.number().int().positive()).max(100).default([]),
  removeAllocationIds: z.array(z.number().int().positive()).max(100).default([]),
});

export const updateServerStartupSchema = z.object({
  startup: z.string().trim().min(1).max(10_000),
  image: z.string().trim().min(1).max(255),
  skipScripts: z.boolean(),
  /** Restart automatically after a crash; unchanged when omitted. */
  autoRestart: z.boolean().optional(),
  variables: z.record(z.string(), z.string()),
});

export const serverPowerSchema = z.object({ action: z.enum(['start', 'stop', 'restart', 'kill']) });

/** Result of a change that must also reach the daemon. */
export const daemonSyncSchema = z.object({
  daemonSynced: z.boolean(),
  daemonError: z.string().nullable(),
});

// ---- Database hosts ------------------------------------------------------------------------

export const databaseHostSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  host: z.string(),
  port: z.number().int(),
  publicHost: z.string().nullable(),
  username: z.string(),
  maxDatabases: z.number().int(),
  nodeId: z.number().int().nullable(),
  /** Databases created on it so far. */
  databaseCount: z.number().int(),
  createdAt: isoDateSchema,
});
export type DatabaseHost = z.infer<typeof databaseHostSchema>;

export const databaseHostInputSchema = z.object({
  name: z.string().trim().min(1).max(191),
  host: z.string().trim().min(1).max(255),
  port: z.number().int().min(1).max(65535).default(3306),
  publicHost: z.string().trim().max(255).nullable().default(null),
  username: z.string().trim().min(1).max(80),
  /** Omit on update to keep the stored password. */
  password: z.string().max(500).optional(),
  maxDatabases: z.number().int().min(0).max(100_000).default(0),
  nodeId: z.number().int().positive().nullable().default(null),
});

export const databaseHostTestSchema = z.object({
  host: z.string().trim().min(1).max(255),
  port: z.number().int().min(1).max(65535),
  username: z.string().trim().min(1).max(80),
  password: z.string().max(500),
});

// ---- Transfers -----------------------------------------------------------------------------

export const transferServerSchema = z.object({
  nodeId: z.number().int().positive(),
  /** The main port on the new node. */
  allocationId: z.number().int().positive(),
  additionalAllocationIds: z.array(z.number().int().positive()).max(100).default([]),
});

export const transferStatusSchema = z.object({
  id: z.number().int(),
  fromNodeId: z.number().int(),
  toNodeId: z.number().int(),
  status: z.enum(['running', 'successful', 'failed']),
  error: z.string().nullable(),
  createdAt: isoDateSchema,
});
export type TransferStatus = z.infer<typeof transferStatusSchema>;
